import React, { useState, useEffect } from 'react';
import { useTranslation } from '@/store/languageStore';
import type {
  ProjectTask,
  ProjectSource,
  ProjectMilestone,
  TaskStatus,
  TaskPriority,
  ProjectLeaderCandidate,
} from '@/types/projects';
import { TASK_STATUSES } from '@/types/projects';
import { projectApi } from '@/services/projects/projectApi';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { TaskSubtaskEditor, type SubtaskDraft } from './TaskSubtaskEditor';

import DateInput from '@/components/common/DateInput';

function candidateLabel(user: ProjectLeaderCandidate, globalAdminLabel: string): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email;
  return user.is_global_admin ? `${name} (${globalAdminLabel})` : name;
}

interface Props {
  source: ProjectSource;
  projectId: number;
  customerId?: string;
  task?: ProjectTask | null;
  milestones: ProjectMilestone[];
  allTasks: ProjectTask[];
  onClose: () => void;
  onSaved: () => void;
}

export const TaskModal: React.FC<Props> = ({
  source, projectId, customerId, task, milestones, allTasks, onClose, onSaved,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<{
    title: string;
    description: string;
    status: TaskStatus;
    priority: TaskPriority;
    milestone_id: string | number;
    assigned_to: string;
    planned_start: string;
    planned_end: string;
    estimated_hours: string;
    component: string;
    feature_code: string;
    github_issue_url: string;
    tech_debt_tag: string;
    depends_on_task_id: string | number;
  }>({
    title: '',
    description: '',
    status: 'backlog',
    priority: 'normal',
    milestone_id: '',
    assigned_to: '',
    planned_start: '',
    planned_end: '',
    estimated_hours: '',
    component: 'other',
    feature_code: '',
    github_issue_url: '',
    tech_debt_tag: 'none',
    depends_on_task_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [assigneeCandidates, setAssigneeCandidates] = useState<ProjectLeaderCandidate[]>([]);
  const [subtasks, setSubtasks] = useState<SubtaskDraft[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const list = source === 'crm'
          ? await projectApi.listManagerCandidates('crm', customerId)
          : await projectApi.listManagerCandidates('dev');
        setAssigneeCandidates(list);
      } catch {
        setAssigneeCandidates([]);
      }
    };
    load();
  }, [source, customerId]);

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title,
        description: task.description || '',
        status: task.status,
        priority: task.priority,
        milestone_id: task.milestone_id || '',
        assigned_to: task.assigned_to || '',
        planned_start: task.planned_start?.slice(0, 10) || '',
        planned_end: task.planned_end?.slice(0, 10) || '',
        estimated_hours: task.estimated_hours?.toString() || '',
        component: task.component || 'other',
        feature_code: task.feature_code || '',
        github_issue_url: task.github_issue_url || '',
        tech_debt_tag: task.tech_debt_tag || 'none',
        depends_on_task_id: '',
      });
      setSubtasks(
        (task.subtasks || []).map((s) => ({
          id: s.id,
          title: s.title,
          is_done: s.is_done,
        }))
      );
    } else {
      setSubtasks([]);
    }
  }, [task]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        title: form.title,
        description: form.description || undefined,
        status: form.status,
        priority: form.priority,
        milestone_id: form.milestone_id ? Number(form.milestone_id) : undefined,
        assigned_to: form.assigned_to || undefined,
        planned_start: form.planned_start || undefined,
        planned_end: form.planned_end || undefined,
        estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : undefined,
        subtasks: subtasks
          .filter((s) => s.title.trim())
          .map((s, index) => ({
            id: s.id,
            title: s.title.trim(),
            is_done: s.is_done,
            sort_order: index,
          })),
      };
      if (source === 'dev') {
        payload.component = form.component;
        payload.feature_code = form.feature_code || undefined;
        payload.github_issue_url = form.github_issue_url || undefined;
        payload.tech_debt_tag = form.tech_debt_tag;
      }

      let savedTask: ProjectTask;
      if (task) {
        savedTask = await projectApi.updateTask(source, task.id, payload);
      } else {
        savedTask = await projectApi.createTask(source, projectId, payload);
      }

      if (form.depends_on_task_id && savedTask) {
        await projectApi.createDependency(source, savedTask.id, Number(form.depends_on_task_id));
      }

      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!task || !confirm(t('common.confirmDelete'))) return;
    await projectApi.deleteTask(source, task.id);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-bgContainer rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-textDefault">
            {task ? t('projects.editTask') : t('projects.addTask')}
          </h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-bgElevated rounded">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">{t('projects.taskTitle')}</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
            />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">{t('common.description')}</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
            />
          </div>
          <TaskSubtaskEditor subtasks={subtasks} onChange={setSubtasks} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('common.status')}</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as TaskStatus })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
              >
                {TASK_STATUSES.map((s) => (
                  <option key={s} value={s}>{t(`projects.taskStatus.${s}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('projects.priority')}</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value as TaskPriority })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
              >
                {['low', 'normal', 'high', 'urgent'].map((p) => (
                  <option key={p} value={p}>{t(`projects.priorityLevel.${p}`)}</option>
                ))}
              </select>
            </div>
          </div>
          {milestones.length > 0 && (
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('projects.milestone')}</label>
              <select
                value={form.milestone_id}
                onChange={(e) => setForm({ ...form, milestone_id: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
              >
                <option value="">—</option>
                {milestones.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm text-textSecondary mb-1">{t('projects.assignedTo')}</label>
            <select
              value={form.assigned_to}
              onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
            >
              <option value="">{t('projects.notAssigned')}</option>
              {assigneeCandidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {candidateLabel(u, t('projects.globalAdmin'))}
                </option>
              ))}
            </select>
            <p className="text-xs text-textSecondary mt-1">
              {source === 'crm' ? t('projects.managerCandidatesHint') : t('projects.ownerCandidatesHint')}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('projects.plannedStart')}</label>
              <DateInput value={form.planned_start}
                onChange={(e) => setForm({ ...form, planned_start: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
              />
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('projects.plannedEnd')}</label>
              <DateInput value={form.planned_end}
                onChange={(e) => setForm({ ...form, planned_end: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
              />
            </div>
          </div>
          {source === 'dev' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-textSecondary mb-1">{t('projects.component')}</label>
                  <select
                    value={form.component}
                    onChange={(e) => setForm({ ...form, component: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
                  >
                    {['backend', 'frontend', 'mobile', 'gateway', 'infra', 'other'].map((c) => (
                      <option key={c} value={c}>{t(`projects.components.${c}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-textSecondary mb-1">{t('projects.featureCode')}</label>
                  <input
                    value={form.feature_code}
                    onChange={(e) => setForm({ ...form, feature_code: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">GitHub</label>
                <input
                  value={form.github_issue_url}
                  onChange={(e) => setForm({ ...form, github_issue_url: e.target.value })}
                  placeholder="https://github.com/..."
                  className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
                />
              </div>
            </>
          )}
          {!task && allTasks.length > 0 && (
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('projects.dependsOn')}</label>
              <select
                value={form.depends_on_task_id}
                onChange={(e) => setForm({ ...form, depends_on_task_id: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
              >
                <option value="">—</option>
                {allTasks.map((t) => (
                  <option key={t.id} value={t.id}>{t.title}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex justify-between pt-2">
            {task && (
              <button type="button" onClick={handleDelete} className="text-red-600 text-sm hover:underline">
                {t('common.delete')}
              </button>
            )}
            <div className="flex gap-2 ml-auto">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded-lg">
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
